// EmojiPickerChat.tsx
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';

// Add a new literal to the Placement type
type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'top-left-of';

interface EmojiPickerProps {
  visible: boolean;
  onSelect: (emoji: any) => void;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  placement?: Placement;                // default: 'bottom-start'
  offset?: number;                      // default: 6
  theme?: 'light' | 'dark';             // default: 'dark'
  closeOnSelect?: boolean;              // default: false (keep open after click)
  closeOnEsc?: boolean;                 // default: false
  closeOnScrollOrResize?: boolean;      // default: false
}

const EmojiPickerChat: React.FC<EmojiPickerProps> = ({
  visible,
  onSelect,
  onClose,
  anchorRef,
  placement = 'bottom-start',
  offset = 6,
  theme = 'dark',
  closeOnSelect = false,
  closeOnEsc = false,
  closeOnScrollOrResize = false,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ top: 0, left: 0, position: 'fixed' });

  // Position relative to the anchor (fixed to viewport)
  useEffect(() => {
    if (!visible) return;
    const el = anchorRef?.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const pos: React.CSSProperties = { position: 'fixed' };
    const w = 340; // approximate width of emoji-mart panel
    const h = 400; // approximate height
    const gap = offset ?? 6;
    const leftOf = rect.left - w;   // panel entirely to the LEFT of the button
    const topAbove = rect.top - h - offset;
    const topBelow = rect.bottom + offset;
    const leftStart = rect.left;
    const leftEnd = rect.right - w;
    

    switch (placement) {
      case 'top-left-of':           // NEW ✨
  pos.left = Math.max(8, leftOf);
  pos.top  = Math.max(8, topAbove - 45); // ⬅️ lift the picker 20px higher
  break;

      case 'bottom-end':
        pos.left = Math.max(8, leftEnd);
        pos.top  = Math.max(8, topBelow);
        break;
      case 'top-start':
        pos.left = Math.max(8, leftStart);
        pos.top  = Math.max(8, topAbove);
        break;
      case 'top-end':
        pos.left = Math.max(8, leftEnd);
        pos.top  = Math.max(8, topAbove);
        break;
      case 'bottom-start':
      default:
        pos.left = Math.max(8, leftStart);
        pos.top  = Math.max(8, topBelow);
    }

    setStyle(pos);
  }, [visible, anchorRef, placement, offset]);

  // Close on outside click / ESC / scroll / resize (as configured)
  useEffect(() => {
    if (!visible) return;

    const handleDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const inPanel = panelRef.current?.contains(t);
      const inAnchor = anchorRef?.current?.contains(t);
      if (!inPanel && !inAnchor) onClose();
    };

    const handleKey = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleDown);
    if (closeOnEsc) document.addEventListener('keydown', handleKey);
    if (closeOnScrollOrResize) {
      window.addEventListener('scroll', onClose, true);
      window.addEventListener('resize', onClose);
    }

    return () => {
      document.removeEventListener('mousedown', handleDown);
      if (closeOnEsc) document.removeEventListener('keydown', handleKey);
      if (closeOnScrollOrResize) {
        window.removeEventListener('scroll', onClose, true);
        window.removeEventListener('resize', onClose);
      }
    };
  }, [visible, onClose, anchorRef, closeOnEsc, closeOnScrollOrResize]);

  if (!visible) return null;

  const handleSelect = (emoji: any) => {
    onSelect(emoji);
    if (closeOnSelect) onClose();
  };

  const panel = (
    <div
      ref={panelRef}
      style={{
        zIndex: 10000,
        borderRadius: 10,
        boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
        ...style,
      }}
    >
      <Picker data={data} onEmojiSelect={handleSelect} theme={theme} />
    </div>
  );

  return ReactDOM.createPortal(panel, document.body);
};

export default EmojiPickerChat;
